import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useDebounced } from '../../library/useDebounced'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { useMediaSrc } from '../../library/useMediaSrc'
import { chatFanId, toChatRow, usersFromListAnswer, type ChatRow, type Raw } from '../chatRows'
import { onlineNow, spendersReady, useOnlineSpenders } from '../onlineSpenders'
import { OnlineSpendersBlock } from '../sideBlocks'
import { useSectionsOpen } from '../../chat/sectionsOpen'
import { PhoneSheet } from './PhoneSheet'

/** What the phone list header shows about the open Account and can do (the console fills it in). */
export interface PhoneListOptions {
  massHref: string
  onScheduled: () => void
  /** Left out when the person may not read Statistics. */
  onStats?: () => void
}

interface Props {
  accountId: string
  options: PhoneListOptions
  search: { open: boolean; typed: string; setTyped: (v: string) => void; start: () => void; close: () => void }
  onOpen: (fanId: string, name: string) => void
  /** The chips, under the title. */
  children: ReactNode
}

/**
 * The phone list's top (handoff-19 Mobile Chat Prototype): "Messages" with
 * Search and New, the spenders-online bar and the chips. Search swaps it for a
 * search field. The Account and Workspace sit in the tab bar's accounts corner.
 */
export function PhoneListHeader({ accountId, options, search, onOpen, children }: Props) {
  const { t } = useTranslation('chats')
  const [sheet, setSheet] = useState<'new' | 'fan' | 'spenders' | null>(null)

  if (search.open) {
    return (
      <div className="ph-head is-search">
        <div className="ph-search">
          <span className="ph-field">
            <FeatherIcon name="search" size={16} />
            <input
              type="search"
              autoFocus
              value={search.typed}
              placeholder={t('phone.searchPlaceholder')}
              aria-label={t('search.label')}
              autoComplete="off"
              data-1p-ignore=""
              data-lpignore="true"
              onChange={(e) => search.setTyped(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && search.close()}
            />
          </span>
          <button type="button" className="ph-text-btn" onClick={search.close}>
            {t('phone.cancel')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="ph-head">
      <div className="ph-title-row">
        <h1 className="ph-title">{t('title')}</h1>
        <button type="button" className="ph-icon-btn" aria-label={t('search.open')} onClick={search.start}>
          <FeatherIcon name="search" size={20} />
        </button>
        <button type="button" className="ph-icon-btn" aria-label={t('phone.new')} aria-haspopup="dialog" onClick={() => setSheet('new')}>
          <FeatherIcon name="edit" size={20} />
        </button>
      </div>
      <SpendersBar accountId={accountId} onOpen={() => setSheet('spenders')} />
      {children}

      <PhoneSheet open={sheet === 'new'} onClose={() => setSheet(null)} label={t('phone.new')}>
        <div className="ph-menu">
          <button type="button" className="ph-menu-item" onClick={() => setSheet('fan')}>
            <FeatherIcon name="message-circle" size={19} />
            {t('phone.messageFan')}
          </button>
          <Link className="ph-menu-item" to={options.massHref} onClick={() => setSheet(null)}>
            <FeatherIcon name="send" size={19} />
            {t('tools.newMass')}
          </Link>
          <button
            type="button"
            className="ph-menu-item"
            onClick={() => {
              setSheet(null)
              options.onScheduled()
            }}
          >
            <FeatherIcon name="calendar" size={19} />
            {t('tools.scheduled')}
          </button>
          {options.onStats && (
            <button
              type="button"
              className="ph-menu-item"
              onClick={() => {
                setSheet(null)
                options.onStats?.()
              }}
            >
              <FeatherIcon name="bar-chart" size={19} />
              {t('tools.stats')}
            </button>
          )}
        </div>
      </PhoneSheet>

      <PhoneSheet open={sheet === 'fan'} onClose={() => setSheet(null)} label={t('phone.messageFan')} title={t('phone.messageFan')}>
        {sheet === 'fan' && (
          <FanPicker
            onPick={(fanId, name) => {
              setSheet(null)
              onOpen(fanId, name)
            }}
          />
        )}
      </PhoneSheet>

      <PhoneSheet open={sheet === 'spenders'} onClose={() => setSheet(null)} label={t('phone.spendersTitle')}>
        {sheet === 'spenders' && (
          <SpendersSheet
            accountId={accountId}
            onPick={(fanId, name) => {
              setSheet(null)
              onOpen(fanId, name)
            }}
          />
        )}
      </PhoneSheet>
    </div>
  )
}

/** "3 spenders online · Ann, Bo, …" — only while Keeper tracks the Account. */
function SpendersBar({ accountId, onOpen }: { accountId: string; onOpen: () => void }) {
  const { t } = useTranslation('chats')
  const { state } = useOnlineSpenders(accountId)
  if (!spendersReady(state)) return null
  const online = onlineNow(state)
  const names = online.map((s) => s.name || s.username || s.fan_id).join(', ')
  return (
    <button type="button" className="ph-spenders" onClick={onOpen}>
      <span className={`ph-spenders-dot ${online.length ? '' : 'is-idle'}`} aria-hidden="true" />
      <span className="ph-spenders-count">{t('phone.spendersOnline', { count: online.length })}</span>
      <span className="ph-spenders-names">{names}</span>
      <FeatherIcon name="chevron-right" size={16} />
    </button>
  )
}

function SpendersSheet({ accountId, onPick }: { accountId: string; onPick: (fanId: string, name: string) => void }) {
  const sections = useSectionsOpen()
  return (
    <div className="ph-sheet-block">
      <OnlineSpendersBlock accountId={accountId} sections={sections} onOpenChat={(_acc, fanId, name) => onPick(fanId, name)} />
    </div>
  )
}

/** Find a fan on this Account (OnlyFans' chat search) and open their chat. */
function FanPicker({ onPick }: { onPick: (fanId: string, name: string) => void }) {
  const { t } = useTranslation('chats')
  const { gateway } = useLibraryDeps()
  const [typed, setTyped] = useState('')
  const query = useDebounced(typed.trim(), 350)
  const [found, setFound] = useState<ChatRow[] | 'busy' | 'error' | null>(null)
  useEffect(() => {
    if (!query) return setFound(null)
    let live = true
    setFound('busy')
    ;(async () => {
      const data = await gateway<{ list?: Raw[] }>('/v1/chats/get', { limit: 10, offset: 0, order: 'recent', skip_users: 'none', query })
      const chats = (data?.list ?? []).filter((c) => chatFanId(c))
      let rows = chats.map((c) => toChatRow(c, c.withUser as Raw | undefined))
      // OnlyFans may leave fans unnamed: users/list names them (as Workspace's search does).
      const unnamed = rows.filter((r) => !r.named).map((r) => r.fanId)
      if (unnamed.length) {
        const users = new Map(usersFromListAnswer(await gateway('/v1/users/list', { user_ids: unnamed, param_name: 'm' }).catch(() => null)).map((u) => [String(u.id), u]))
        rows = chats.map((c) => toChatRow(c, { ...((c.withUser as Raw) ?? {}), ...(users.get(chatFanId(c)) ?? {}) }))
      }
      if (live) setFound(rows)
    })().catch(() => live && setFound('error'))
    return () => {
      live = false
    }
  }, [gateway, query])
  return (
    <div className="ph-picker">
      <span className="ph-field">
        <FeatherIcon name="search" size={16} />
        <input type="search" autoFocus value={typed} placeholder={t('phone.searchFans')} aria-label={t('phone.searchFans')} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
      </span>
      {found === 'busy' ? (
        <span className="ph-picker-note" aria-busy="true">
          <span className="chat-list-spinner" />
        </span>
      ) : found === 'error' ? (
        <span className="ph-picker-note">{t('phone.searchFailed')}</span>
      ) : found && !found.length ? (
        <span className="ph-picker-note">{t('phone.noMatches')}</span>
      ) : !found ? (
        <span className="ph-picker-note">{t('phone.searchFansHint')}</span>
      ) : (
        <div className="ph-picker-list">
          {found.map((r) => (
            <button key={r.fanId} type="button" className="ph-person" onClick={() => onPick(r.fanId, r.name)}>
              <PersonPic row={r} />
              <span className="ph-person-main">
                <span className="ph-person-name">{r.name}</span>
                <span className="ph-person-sub">{r.handle}</span>
              </span>
              {r.spent != null && <span className="ph-person-spent">${r.spent.toFixed(0)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PersonPic({ row }: { row: ChatRow }) {
  const pic = useMediaSrc(row.avatar ?? '')
  return (
    <span className="ph-person-pic" ref={pic.ref as React.RefObject<HTMLSpanElement>}>
      {pic.src ? <img src={pic.src} alt="" loading="lazy" /> : <FeatherIcon name="user" size={18} />}
      {row.online && <span className="ph-row-online is-small" aria-hidden="true" />}
    </span>
  )
}
